// src/pages/InventoryLogsPage.jsx
import React, { useState, useEffect } from 'react';
import { History, Search, Download, Calendar, Store } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { inventoryApi, storeApi } from '../api';
import { exportToCSV } from '../utils/csvExport';

// change_type 中文对照——覆盖这次新加的5种，也覆盖批次系统上线前遗留在历史数据里
// 的老值（sale/transfer_in/transfer_out/adjust/return/delete），不然老记录还是显示英文
const CHANGE_TYPE_LABELS = {
  restock:          '进货',
  loss:             '损耗/盘亏',
  found:            '盘盈',
  return_restock:   '可上架退货',
  return_writeoff:  '报废退货',
  // 以下是批次系统上线前的历史遗留值
  sale:             '销售扣减',
  return:           '订单退回',
  adjust:           '库存修正（旧）',
  transfer_in:      '划拨入库',
  transfer_out:     '划拨出库',
  delete:           '删除商品',
};

const CHANGE_TYPE_COLOR = {
  restock:          'bg-green-100 text-green-700',
  loss:             'bg-red-100 text-red-700',
  found:            'bg-blue-100 text-blue-700',
  return_restock:   'bg-teal-100 text-teal-700',
  return_writeoff:  'bg-gray-200 text-gray-700',
  sale:             'bg-orange-100 text-orange-700',
  return:           'bg-purple-100 text-purple-700',
  adjust:           'bg-gray-100 text-gray-600',
  transfer_in:      'bg-cyan-100 text-cyan-700',
  transfer_out:     'bg-amber-100 text-amber-700',
  delete:           'bg-red-50 text-red-500',
};

const InventoryLogsPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productKeyword, setProductKeyword] = useState('');
  const [changeType, setChangeType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // 门店筛选——只有总店账号能看到，默认空字符串代表"全部门店"（和后端约定一致：
  // 不传 store_id 就是全部门店混在一起，这是原来就有的默认行为，不受这次改动影响）
  const [stores, setStores] = useState([]);
  const [viewStoreId, setViewStoreId] = useState('');

  useEffect(() => {
    if (isHQ) {
      storeApi.getList().then(res => {
        if (res.success) setStores(res.data || []);
      }).catch(err => console.error('加载门店列表失败:', err));
    }
  }, [isHQ]);

  useEffect(() => {
    loadLogs();
  }, [changeType, startDate, endDate, viewStoreId]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (changeType) params.change_type = changeType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (isHQ && viewStoreId) params.store_id = viewStoreId;
      const res = await inventoryApi.getLogs(params);
      if (res.success) setLogs(res.data || []);
    } catch (err) {
      console.error('加载库存变动记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!productKeyword) return true;
    const kw = productKeyword.toLowerCase();
    return log.product_name?.toLowerCase().includes(kw) || log.barcode?.includes(kw);
  });

  // 汇总卡片——跟着当前全部筛选条件走（门店/商品关键词/类型/日期区间），
  // 和下面表格实际展示的内容保持一致
  const totalLogCount = filteredLogs.length;
  const distinctProductCount = new Set(filteredLogs.map(l => l.product_id)).size;
  const totalIncrease = filteredLogs.reduce((sum, l) => sum + (l.quantity > 0 ? l.quantity : 0), 0);
  const totalDecrease = filteredLogs.reduce((sum, l) => sum + (l.quantity < 0 ? Math.abs(l.quantity) : 0), 0);

  const handleExport = () => {
    exportToCSV(
      filteredLogs.map(log => ({
        ...log,
        change_type_label: CHANGE_TYPE_LABELS[log.change_type] || log.change_type,
        created_at_fmt: new Date(log.created_at).toLocaleString('zh-CN'),
      })),
      [
        { key: 'created_at_fmt', label: '时间' },
        { key: 'product_name', label: '商品名称' },
        { key: 'barcode', label: '条码' },
        { key: 'change_type_label', label: '变动类型' },
        { key: 'quantity', label: '变动数量' },
        { key: 'before_stock', label: '变动前库存' },
        { key: 'after_stock', label: '变动后库存' },
        { key: 'operator_name', label: '操作人' },
        { key: 'notes', label: '备注' },
      ],
      '库存变动记录'
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">库存变动记录</h2>
          <p className="text-xs text-gray-600 mt-1">进货、损耗、销售等所有库存变动的历史流水</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} disabled={filteredLogs.length === 0}
            className="flex items-center gap-1.5 border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
            <Download className="w-4 h-4" />导出
          </button>
          {/* 门店筛选——只有总店账号能看到，放在最右侧 */}
          {isHQ && (
            <div className="relative">
              <Store className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select value={viewStoreId} onChange={e => setViewStoreId(e.target.value)}
                className="pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white appearance-none">
                <option value="">全部门店</option>
                {stores.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{String(s.id) === String(currentUser?.store_id) ? '（本店）' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* 汇总统计卡片——跟着门店筛选器+其他筛选条件一起走 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">记录数</p>
          <p className="text-2xl font-bold mt-1">{totalLogCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">涉及商品种类</p>
          <p className="text-2xl font-bold mt-1">{distinctProductCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">累计增加</p>
          <p className="text-2xl font-bold mt-1 text-green-600">+{totalIncrease.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">累计减少</p>
          <p className="text-2xl font-bold mt-1 text-red-600">-{totalDecrease.toLocaleString()}</p>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="搜索商品名称/条码"
            value={productKeyword} onChange={e => setProductKeyword(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={changeType} onChange={e => setChangeType(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">全部类型</option>
          {Object.entries(CHANGE_TYPE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400" />
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="px-2 py-2 border rounded-lg text-sm" />
          <span className="text-gray-400">至</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="px-2 py-2 border rounded-lg text-sm" />
        </div>
      </div>

      {/* 记录表格 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无库存变动记录</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-gray-500 text-left">
                <th className="px-4 py-3">时间</th>
                <th className="px-4 py-3">商品</th>
                <th className="px-4 py-3">类型</th>
                <th className="px-4 py-3 text-right">变动数量</th>
                <th className="px-4 py-3 text-right">变动前</th>
                <th className="px-4 py-3 text-right">变动后</th>
                <th className="px-4 py-3">操作人</th>
                <th className="px-4 py-3">备注</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{log.product_name || '（商品已删除）'}</p>
                    <p className="text-xs text-gray-400">{log.barcode}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHANGE_TYPE_COLOR[log.change_type] || 'bg-gray-100 text-gray-600'}`}>
                      {CHANGE_TYPE_LABELS[log.change_type] || log.change_type}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${log.quantity > 0 ? 'text-green-600' : log.quantity < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {log.quantity > 0 ? '+' : ''}{log.quantity}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500">{log.before_stock}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{log.after_stock}</td>
                  <td className="px-4 py-3 text-gray-600">{log.operator_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate" title={log.notes}>{log.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryLogsPage;